import { Button, notify } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import { SettingsTabbedContent } from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsTabs } from '../shared/settings-navigation';
import {
  WebhookAccessTab,
  WebhookManageTab,
  WebhookSecretTab,
} from '../webhooks/webhook-detail-tabs';
import type {
  WebhookCredential,
  WebhookDetail,
  WebhookItemContext,
} from '../webhooks/webhook-types';

function webhookRequestKey(action: string, webhookId?: string) {
  return `inbound-webhooks:${action}${webhookId ? `:${webhookId}` : ''}`;
}

function useWebhookCredentials(backendAvailable: boolean) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [webhooks, setWebhooks] = useState<WebhookCredential[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    if (!backendAvailable) {
      setLoading(false);
      return;
    }
    try {
      const webhookSummaries = await defaultWorkspaceApiClient.requestJson<
        Array<{
          credentialId: string;
          name: string;
          webhookUrl: string | null;
          status: string;
          createdAt: string;
        }>
      >({
        workspaceId,
        path: `/api/workspace/${workspaceId}/inbound-webhooks`,
        category: 'settings',
        key: webhookRequestKey('list'),
        mode: 'background',
        priority: 'visible',
      });

      setWebhooks(
        webhookSummaries.map(summary => ({
          id: summary.credentialId,
          name: summary.name,
          service: 'webhook',
          status: summary.status,
          createdAt: summary.createdAt,
          webhookUrl: summary.webhookUrl,
        }))
      );
    } catch (err) {
      notify.error({
        title: `Failed to load webhooks: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { webhooks, loading, refresh };
}

function useWebhookDetail(
  _workspaceId: string,
  _webhook: WebhookCredential,
  _expanded: boolean
) {
  const [detail, setDetail] = useState<WebhookDetail | null>(null);
  return { detail, detailLoading: false, setDetail };
}

function useWebhookActions(args: {
  workspaceId: string;
  webhook: WebhookCredential;
  draftName: string;
  setDraftName: (value: string) => void;
  setRenaming: (value: boolean) => void;
  setCopied: (value: boolean) => void;
  setDetail: Dispatch<SetStateAction<WebhookDetail | null>>;
  onChanged: () => void;
  t: ReturnType<typeof useI18n>;
}) {
  const {
    workspaceId,
    webhook,
    draftName,
    setDraftName,
    setRenaming,
    setCopied,
    setDetail,
    onChanged,
    t,
  } = args;
  const [rotating, setRotating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleCopyUrl = useCallback(
    async (resolvedUrl: string | null) => {
      if (!resolvedUrl) return;
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    [setCopied]
  );

  const handleRename = useCallback(async () => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === webhook.name) {
      setRenaming(false);
      setDraftName(webhook.name);
      return;
    }

    try {
      await defaultWorkspaceApiClient.request({
        workspaceId,
        path: `/api/workspace/${workspaceId}/inbound-webhooks/${webhook.id}`,
        init: {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        },
        category: 'settings',
        key: webhookRequestKey('rename', webhook.id),
        mode: 'foreground',
        priority: 'foreground',
      });
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.automations.webhook.renamed'
          ]?.() ?? 'Webhook renamed',
      });
      setRenaming(false);
      onChanged();
    } catch (err) {
      notify.error({
        title: `Failed to rename: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  }, [
    draftName,
    onChanged,
    setDraftName,
    setRenaming,
    t,
    webhook.id,
    webhook.name,
    workspaceId,
  ]);

  const handleRotate = useCallback(async () => {
    setRotating(true);
    try {
      const data = await defaultWorkspaceApiClient.requestJson<{
        webhookUrl: string;
      }>({
        workspaceId,
        path: `/api/workspace/${workspaceId}/inbound-webhooks/${webhook.id}/rotate`,
        init: { method: 'POST' },
        category: 'settings',
        key: webhookRequestKey('rotate', webhook.id),
        mode: 'foreground',
        priority: 'foreground',
      });
      setDetail(prev =>
        prev
          ? { ...prev, webhookUrl: data.webhookUrl }
          : {
              credentialId: webhook.id,
              webhookUrl: data.webhookUrl,
              automationTitle: null,
              docId: '',
              docTitle: null,
            }
      );
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.automations.webhook.rotated'
          ]?.() ?? 'Webhook token rotated',
      });
      onChanged();
    } catch (err) {
      notify.error({
        title: `Failed to rotate: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setRotating(false);
    }
  }, [onChanged, setDetail, t, webhook.id, workspaceId]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await defaultWorkspaceApiClient.request({
        workspaceId,
        path: `/api/workspace/${workspaceId}/inbound-webhooks/${webhook.id}`,
        init: { method: 'DELETE' },
        category: 'settings',
        key: webhookRequestKey('delete', webhook.id),
        mode: 'foreground',
        priority: 'foreground',
      });

      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.automations.webhook.deleted'
          ]?.() ?? 'Webhook deleted',
      });
      onChanged();
    } catch (err) {
      notify.error({
        title: `Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setDeleting(false);
    }
  }, [onChanged, t, webhook.id, workspaceId]);

  return {
    deleting,
    rotating,
    handleCopyUrl,
    handleDelete,
    handleRename,
    handleRotate,
  };
}

const WebhookSummary: FC<{
  context: WebhookItemContext;
  hasAccessControl: boolean;
  t: ReturnType<typeof useI18n>;
}> = ({ context, hasAccessControl, t }) => {
  const { webhook } = context;
  const statusLabel =
    webhook.status === 'active'
      ? (t['com.streamlain.streamlain.common.active']?.() ?? 'Aktiv')
      : (t['com.streamlain.streamlain.common.inactive']?.() ?? 'Inaktiv');

  return (
    <>
      <SettingsBadge variant={webhook.status === 'active' ? 'active' : 'error'}>
        {statusLabel}
      </SettingsBadge>
      <SettingsBadge>
        {t['com.streamlain.settings.workspace.webhooks.badge.shared']?.() ??
          'Shared endpoint'}
      </SettingsBadge>
      {hasAccessControl ? (
        <SettingsBadge>
          {t['com.streamlain.settings.workspace.webhooks.badge.access']?.() ??
            'Access control'}
        </SettingsBadge>
      ) : null}
    </>
  );
};

const WebhookItem: FC<{
  webhook: WebhookCredential;
  workspaceId: string;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  canManageEndpoints: boolean;
  hasAccessControl: boolean;
  canManageAccess: boolean;
}> = props => {
  type WebhookTab = 'manage' | 'secret' | 'access';
  const {
    webhook,
    workspaceId,
    expanded,
    onToggle,
    onChanged,
    canManageEndpoints,
    hasAccessControl,
    canManageAccess,
  } = props;
  const t = useI18n();
  const { detail, detailLoading, setDetail } = useWebhookDetail(
    workspaceId,
    webhook,
    expanded
  );
  const [copied, setCopied] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(webhook.name);
  const [activeTab, setActiveTab] = useState<WebhookTab>(
    canManageEndpoints ? 'manage' : 'secret'
  );

  useEffect(() => {
    setDraftName(webhook.name);
  }, [webhook.name]);

  useEffect(() => {
    if (!expanded) {
      setActiveTab(canManageEndpoints ? 'manage' : 'secret');
    }
  }, [canManageEndpoints, expanded]);

  useEffect(() => {
    if (!canManageEndpoints && activeTab === 'manage') {
      setActiveTab('secret');
    }
  }, [activeTab, canManageEndpoints]);

  const resolvedUrl = detail?.webhookUrl ?? webhook.webhookUrl;
  const canRevealToken = !!resolvedUrl;

  const context: WebhookItemContext = {
    webhook,
    detail,
    resolvedUrl,
  };

  const {
    deleting,
    rotating,
    handleCopyUrl,
    handleDelete,
    handleRename,
    handleRotate,
  } = useWebhookActions({
    workspaceId,
    webhook,
    draftName,
    setDraftName,
    setRenaming,
    setCopied,
    setDetail,
    onChanged,
    t,
  });

  return (
    <SettingsExpandableItem
      dataTestId={`webhook-item-${webhook.id}`}
      expanded={expanded}
      onToggle={onToggle}
      title={webhook.name}
      badges={
        <WebhookSummary
          context={context}
          hasAccessControl={hasAccessControl}
          t={t}
        />
      }
      meta={
        <>
          <SettingsMetaItem>
            {t['com.streamlain.settings.workspace.webhooks.meta.created']?.() ??
              'Created'}{' '}
            {new Date(webhook.createdAt).toLocaleDateString()}
          </SettingsMetaItem>
          <SettingsMetaItem>
            {resolvedUrl
              ? (t[
                  'com.streamlain.settings.workspace.webhooks.meta.ready'
                ]?.() ?? 'Ready to receive POST requests')
              : (t[
                  'com.streamlain.settings.workspace.webhooks.meta.missing'
                ]?.() ?? 'Endpoint details unavailable')}
          </SettingsMetaItem>
        </>
      }
    >
      {detailLoading && (
        <SettingsEntityMessage>
          {t[
            'com.streamlain.settings.workspace.automations.webhook.loading-url'
          ]?.() ?? 'Loading webhook URL...'}
        </SettingsEntityMessage>
      )}

      {resolvedUrl || canManageEndpoints ? (
        <SettingsTabbedContent>
          <SettingsTabs
            ariaLabel="Webhook sections"
            activeTab={activeTab}
            onChange={tabId =>
              setActiveTab(tabId as 'manage' | 'secret' | 'access')
            }
            tabs={[
              ...(canManageEndpoints
                ? [
                    {
                      id: 'manage',
                      label:
                        t[
                          'com.streamlain.settings.workspace.webhooks.card.management'
                        ]?.() ?? 'Manage endpoint',
                    },
                  ]
                : []),
              {
                id: 'secret',
                label:
                  t[
                    'com.streamlain.settings.workspace.webhooks.card.secret'
                  ]?.() ?? 'Sensitive URL and token',
              },
              {
                id: 'access',
                label:
                  t[
                    'com.streamlain.settings.workspace.webhooks.card.access'
                  ]?.() ?? 'Permissions and access control',
              },
            ]}
          />

          {activeTab === 'manage' && (
            <WebhookManageTab
              webhook={webhook}
              canManageEndpoints={canManageEndpoints}
              draftName={draftName}
              renaming={renaming}
              rotating={rotating}
              deleting={deleting}
              setDraftName={setDraftName}
              setRenaming={setRenaming}
              onRename={() => void handleRename()}
              onRotate={() => void handleRotate()}
              onDelete={() => void handleDelete()}
              canRotateToken={canRevealToken}
            />
          )}

          {activeTab === 'secret' && (
            <WebhookSecretTab
              resolvedUrl={context.resolvedUrl}
              copied={copied}
              onCopy={() => void handleCopyUrl(resolvedUrl)}
            />
          )}

          {activeTab === 'access' && (
            <WebhookAccessTab
              webhookId={webhook.id}
              hasAccessControl={hasAccessControl}
              canManageAccess={canManageAccess}
            />
          )}
        </SettingsTabbedContent>
      ) : (
        !detailLoading && (
          <SettingsEntityMessage>
            {t['com.streamlain.settings.workspace.webhooks.meta.missing']?.() ??
              'Endpoint details unavailable'}
          </SettingsEntityMessage>
        )
      )}
    </SettingsExpandableItem>
  );
};

export const WebhookList: FC<{
  title: string;
  subtitle: string;
  canManageEndpoints: boolean;
  hasAccessControl: boolean;
  canManageAccess: boolean;
  footer?: ReactNode;
}> = ({
  title,
  subtitle,
  canManageEndpoints,
  hasAccessControl,
  canManageAccess,
  footer,
}) => {
  const PAGE_SIZE = 10;
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const { webhooks, loading, refresh } =
    useWebhookCredentials(backendAvailable);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    if (!backendAvailable) {
      setCreating(false);
      return;
    }
    try {
      await defaultWorkspaceApiClient.request({
        workspaceId,
        path: `/api/workspace/${workspaceId}/inbound-webhooks`,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
        category: 'settings',
        key: webhookRequestKey('create'),
        mode: 'foreground',
        priority: 'foreground',
      });
      await refresh();
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.automations.webhook.created'
          ]?.() ?? 'Webhook created',
      });
    } catch (err) {
      notify.error({
        title: `Failed to create: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setCreating(false);
    }
  }, [backendAvailable, refresh, t, workspaceId]);

  const handleChanged = useCallback(() => {
    void refresh();
  }, [refresh]);

  const filteredWebhooks = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return webhooks;
    return webhooks.filter(webhook => {
      return (
        webhook.name.toLowerCase().includes(query) ||
        webhook.status.toLowerCase().includes(query) ||
        (webhook.webhookUrl ?? '').toLowerCase().includes(query)
      );
    });
  }, [searchQuery, webhooks]);

  const pageCount = Math.max(1, Math.ceil(filteredWebhooks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paginatedWebhooks = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredWebhooks.slice(start, start + PAGE_SIZE);
  }, [currentPage, filteredWebhooks]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  return (
    <SettingsSearchableEntityPage
      title={title}
      subtitle={subtitle}
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={
        t[
          'com.streamlain.settings.workspace.webhooks.search.placeholder'
        ]?.() ?? 'Webhooks suchen...'
      }
      action={
        canManageEndpoints ? (
          <Button
            variant="primary"
            onClick={() => void handleCreate()}
            disabled={creating || !backendAvailable}
          >
            {creating
              ? '...'
              : (t[
                  'com.streamlain.settings.workspace.automations.webhook.create'
                ]?.() ?? 'Create webhook')}
          </Button>
        ) : undefined
      }
      loading={loading && backendAvailable}
      hasItems={filteredWebhooks.length > 0}
      loadingContent={
        <SettingsEntityMessage>
          {t[
            'com.streamlain.settings.workspace.automations.webhook.loading'
          ]?.() ?? 'Loading webhooks...'}
        </SettingsEntityMessage>
      }
      emptyContent={
        <SettingsEntityMessage>
          {searchQuery.trim()
            ? (t[
                'com.streamlain.settings.workspace.webhooks.search.empty'
              ]?.() ?? 'Keine Webhooks für diese Suche gefunden.')
            : !backendAvailable
              ? offlineLabel
              : (t[
                  'com.streamlain.settings.workspace.automations.webhook.empty'
                ]?.() ??
                'No webhooks yet. Create one and assign it to an automation trigger.')}
        </SettingsEntityMessage>
      }
      pagination={
        !loading && filteredWebhooks.length > 0
          ? {
              page: currentPage,
              pageCount,
              pageSize: PAGE_SIZE,
              totalItems: filteredWebhooks.length,
              onPageChange: setPage,
              previousLabel:
                t['com.streamlain.pagination.previous']?.() ?? 'Zurueck',
              nextLabel: t['com.streamlain.pagination.next']?.() ?? 'Weiter',
            }
          : undefined
      }
      footer={footer}
    >
      <SettingsEntityList>
        {paginatedWebhooks.map(webhook => (
          <WebhookItem
            key={webhook.id}
            webhook={webhook}
            workspaceId={workspaceId}
            expanded={expandedId === webhook.id}
            onToggle={() =>
              setExpandedId(expandedId === webhook.id ? null : webhook.id)
            }
            onChanged={handleChanged}
            canManageEndpoints={canManageEndpoints}
            hasAccessControl={hasAccessControl}
            canManageAccess={canManageAccess}
          />
        ))}
      </SettingsEntityList>
    </SettingsSearchableEntityPage>
  );
};
